import { FileWarning } from 'lucide-react';
import type { ReactElement } from 'react';

import { InlineError } from '@/components/ui/StatePanel';
import { useMasterList } from '@/features/master-list/use-master-list';
import { useActiveCompany } from '@/lib/active-company-context';

import type { GrantScopeFieldsApi } from '../use-grant-scope-fields';
import { businessToday } from '@/lib/time';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

/** Campos Vence + Motivo + Documentos con lectura (de la empresa activa). */
export function GrantScopeFields({
  fields,
  enabled,
}: {
  fields: GrantScopeFieldsApi;
  enabled: boolean;
}): ReactElement {
  const { activeCompanyId } = useActiveCompany();
  const docsQuery = useMasterList(
    { limit: 100, page: 1 },
    { companyId: activeCompanyId, enabled: enabled && Boolean(activeCompanyId) },
  );
  const rows = docsQuery.data?.data ?? [];
  const { state } = fields;

  return (
    <>
      <label className="fld">
        <span className="fld-lab">Vence</span>
        <input
          type="date"
          className="selector fw"
          value={state.expiresAt}
          min={businessToday(new Date())}
          onChange={(e) => fields.setExpiresAt(e.target.value)}
        />
      </label>
      <label className="fld">
        <span className="fld-lab">Motivo</span>
        <input
          type="text"
          className="selector fw"
          value={state.reason}
          maxLength={500}
          placeholder="Motivo del acceso"
          onChange={(e) => fields.setReason(e.target.value)}
        />
      </label>
      <div className="fld">
        <span className="fld-lab">Documentos con lectura ({state.docs.size})</span>
        <div className="doc-picker">
          {/* Una lista que no cargó (o que aún carga) no se presenta como vacía (UX-ERR-01); una
              recarga que falla conserva las casillas que ya se veían. */}
          {docsQuery.isError && (
            <InlineError
              icon={<FileWarning />}
              onRetry={() => void docsQuery.refetch()}
              retrying={docsQuery.isFetching}
              dense
            >
              {docsQuery.data === undefined
                ? `No se pudieron cargar los documentos. ${errorAdvice(docsQuery.error)}`
                : `No se pudo actualizar la lista de documentos. Se muestran los anteriores.${refreshAdvice(docsQuery.error)}`}
            </InlineError>
          )}
          {docsQuery.isError && docsQuery.data === undefined ? null : docsQuery.isLoading ? (
            <p className="soft" role="status" style={{ fontSize: 12.5, margin: 8 }}>
              Cargando documentos…
            </p>
          ) : rows.length === 0 ? (
            <p className="soft" style={{ fontSize: 12.5, margin: 8 }}>
              Sin documentos disponibles en la empresa activa.
            </p>
          ) : (
            rows.map((d) => (
              <label key={d.id} className="doc-opt">
                <input
                  type="checkbox"
                  checked={state.docs.has(d.id)}
                  onChange={() => fields.toggleDoc(d.id)}
                />
                <span className="code">{d.code}</span> {d.title ?? ''}
              </label>
            ))
          )}
        </div>
      </div>
    </>
  );
}
